import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useWallet } from "@/context/WalletContext";
import { WelockLogo } from "@/components/WelockMark";
import { RobinhoodMark } from "@/components/RobinhoodMark";
import { shortAddr } from "@/lib/api";
import { verifyToken } from "@/lib/api";
import {
  LayoutGrid, Lock, Layers, Clock, Waves, Send, Wallet, Cpu, LogOut, Copy, Check, ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";
import Overview from "@/dashboard/Overview";
import LockerView from "@/dashboard/LockerView";
import Multisender from "@/dashboard/Multisender";
import Portfolio from "@/dashboard/Portfolio";
import Agents from "@/dashboard/Agents";

const NAV = [
  { id: "overview", label: "Overview", icon: LayoutGrid },
  { id: "token-locker", label: "Token Locker", icon: Lock },
  { id: "lp-locker", label: "LP Locker", icon: Layers },
  { id: "vesting", label: "Vesting", icon: Clock },
  { id: "lp-vesting", label: "LP Vesting", icon: Waves },
  { id: "multisender", label: "Multisender", icon: Send },
  { id: "portfolio", label: "Portfolio", icon: Wallet },
  { id: "agents", label: "Agents API", icon: Cpu },
];

const LOCKER_META = {
  "token-locker": { title: "Token Locker", desc: "Lock ERC-20 tokens with a fixed unlock date or vesting schedule." },
  "lp-locker": { title: "LP Locker", desc: "Secure Uniswap V3 and V4 liquidity positions on Robinhood Chain." },
  "vesting": { title: "Vesting", desc: "Create linear or cliff based token distributions for stakeholders." },
  "lp-vesting": { title: "LP Vesting", desc: "Schedule automated liquidity unlocks synced to milestones." },
};

export default function Dashboard() {
  const [view, setView] = useState("overview");
  const { address, disconnect, getAccessToken } = useWallet();
  const [copied, setCopied] = useState(false);
  const [verified, setVerified] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const token = await getAccessToken();
        if (!token) return;
        await verifyToken(token);
        if (alive) setVerified(true);
      } catch {
        /* token verification is non-blocking */
      }
    })();
    return () => { alive = false; };
  }, [getAccessToken]);

  const copyAddr = () => {
    navigator.clipboard.writeText(address);
    setCopied(true);
    toast.success("Address copied");
    setTimeout(() => setCopied(false), 1500);
  };

  const handleDisconnect = () => {
    disconnect();
    navigate("/");
  };

  const renderView = () => {
    if (view === "overview") return <Overview onNavigate={setView} />;
    if (view === "multisender") return <Multisender />;
    if (view === "portfolio") return <Portfolio />;
    if (view === "agents") return <Agents />;
    return <LockerView key={view} product={view} meta={LOCKER_META[view]} />;
  };

  return (
    <div className="flex min-h-screen bg-welock-bg text-welock-textPrimary">
      {/* Sidebar */}
      <aside className="fixed inset-y-0 left-0 hidden w-64 flex-col border-r border-welock-border bg-welock-bg lg:flex">
        <div className="border-b border-welock-border px-6 py-5">
          <button onClick={() => navigate("/")} data-testid="sidebar-logo">
            <WelockLogo />
          </button>
        </div>
        <nav className="flex-1 space-y-1 p-3">
          {NAV.map((n) => {
            const active = view === n.id;
            return (
              <button
                key={n.id}
                data-testid={`nav-${n.id}`}
                onClick={() => setView(n.id)}
                className={`flex w-full items-center gap-3 border-l-2 px-4 py-2.5 text-left font-mono text-xs transition-colors ${
                  active
                    ? "border-welock-accent bg-welock-surface text-welock-textPrimary"
                    : "border-transparent text-welock-textSecondary hover:bg-welock-surface/50 hover:text-welock-textPrimary"
                }`}
              >
                <n.icon size={15} className={active ? "text-welock-accent" : ""} />
                {n.label}
              </button>
            );
          })}
        </nav>
        <div className="border-t border-welock-border p-4">
          <RobinhoodMark size={16} withLabel />
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 lg:ml-64">
        {/* Topbar */}
        <header className="sticky top-0 z-30 flex items-center justify-between border-b border-welock-border bg-welock-bg/90 px-6 py-4 backdrop-blur-md md:px-10">
          <div className="flex items-center gap-2 lg:hidden">
            <WelockLogo />
          </div>
          <div className="hidden font-mono text-xs uppercase tracking-widest text-welock-textMuted lg:block">
            {NAV.find((n) => n.id === view)?.label}
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="wallet-address"
              onClick={copyAddr}
              className="flex items-center gap-2 border border-welock-border px-3 py-2 font-mono text-xs text-welock-textSecondary transition-colors hover:border-welock-hairline hover:text-welock-textPrimary"
            >
              {verified ? (
                <ShieldCheck size={12} className="text-welock-accent" title="Privy verified" />
              ) : (
                <span className="h-1.5 w-1.5 rounded-full bg-welock-accent" />
              )}
              {shortAddr(address)}
              {copied ? <Check size={12} className="text-welock-accent" /> : <Copy size={12} />}
            </button>
            <button
              data-testid="disconnect-btn"
              onClick={handleDisconnect}
              className="flex items-center gap-2 border border-welock-border px-3 py-2 font-mono text-xs text-welock-textSecondary transition-colors hover:border-welock-danger/50 hover:text-welock-danger"
            >
              <LogOut size={13} />
            </button>
          </div>
        </header>

        {/* Mobile nav */}
        <div className="flex gap-1 overflow-x-auto border-b border-welock-border p-2 lg:hidden">
          {NAV.map((n) => (
            <button
              key={n.id}
              onClick={() => setView(n.id)}
              className={`whitespace-nowrap px-3 py-1.5 font-mono text-[11px] ${
                view === n.id ? "bg-welock-surface text-welock-accent" : "text-welock-textSecondary"
              }`}
            >
              {n.label}
            </button>
          ))}
        </div>

        <main className="p-6 md:p-10">{renderView()}</main>
      </div>
    </div>
  );
}
